import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import type { ActivityChatItem } from '@ayocore/activity/common/types/activity-message';

type UseActivityChatScrollControllerParams = {
  selectedThreadId: number | null;
  chatItems: ActivityChatItem[];
  chatLoading: boolean;
  pinsLoading: boolean;
  commentsHasMoreOlder: boolean;
  loadingOlderComments: boolean;
  onLoadOlderComments?: () => void;
  canAutoLoadOlder?: boolean;
  hasNewerMessages?: boolean;
  onLoadNewerComments?: () => void;
  jumpToCommentUuid?: string | null;
  onJumpToCommentHandled?: () => void;
};

export type ActivityChatViewportController = {
  viewportRef: RefObject<HTMLDivElement | null>;
  /**
   * Надежно «приклеивает» скролл к низу после успешной отправки.
   */
  scrollAfterSend: () => void;
  showScrollToBottom: boolean;
  scrollToBottomByUser: () => void;
};

/**
 * Управляет viewport-логикой правой панели Activity:
 * initial scroll, sticky-bottom, jump-to-comment и autoload older/newer.
 */
export function useActivityChatScrollController({
  selectedThreadId,
  chatItems,
  chatLoading,
  pinsLoading,
  commentsHasMoreOlder,
  loadingOlderComments,
  onLoadOlderComments,
  canAutoLoadOlder = false,
  hasNewerMessages = false,
  onLoadNewerComments,
  jumpToCommentUuid,
  onJumpToCommentHandled,
}: UseActivityChatScrollControllerParams): ActivityChatViewportController {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const previousThreadIdRef = useRef<number | null>(null);
  const pendingInitialScrollThreadIdRef = useRef<number | null>(null);
  const initialScrollLoadSeenThreadIdRef = useRef<number | null>(null);
  const previousMessagesCountRef = useRef(0);
  const previousTailItemIdRef = useRef<string | null>(null);
  const lastTopAutoLoadAtRef = useRef(0);
  const lastBottomAutoLoadAtRef = useRef(0);

  const isNearBottom = useCallback((element: HTMLDivElement, threshold: number = 120): boolean => {
    const distanceToBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    return distanceToBottom <= threshold;
  }, []);

  const scrollToBottom = useCallback(() => {
    const element = viewportRef.current;
    if (!element) return;
    element.scrollTop = element.scrollHeight - element.clientHeight;
  }, []);

  const scrollToBottomSmooth = useCallback(() => {
    const element = viewportRef.current;
    if (!element) return;
    element.scrollTo({
      top: element.scrollHeight - element.clientHeight,
      behavior: 'smooth',
    });
  }, []);

  const updateScrollToBottomVisibility = useCallback(
    (element?: HTMLDivElement | null) => {
      const viewport = element ?? viewportRef.current;
      if (!viewport) {
        setShowScrollToBottom(false);
        return;
      }
      const next = !isNearBottom(viewport, 120);
      setShowScrollToBottom(prev => (prev === next ? prev : next));
    },
    [isNearBottom]
  );

  const scrollToBottomDeferred = useCallback(
    (frames: number = 3) => {
      const run = (left: number) => {
        scrollToBottom();
        if (left <= 1) return;
        requestAnimationFrame(() => run(left - 1));
      };

      requestAnimationFrame(() => run(Math.max(1, frames)));
    },
    [scrollToBottom]
  );

  useEffect(() => {
    const threadId = selectedThreadId ?? null;
    if (threadId === previousThreadIdRef.current) return;
    previousThreadIdRef.current = threadId;
    setShowScrollToBottom(false);
    pendingInitialScrollThreadIdRef.current = threadId;
    initialScrollLoadSeenThreadIdRef.current = null;
    previousMessagesCountRef.current = 0;
    previousTailItemIdRef.current = null;
  }, [selectedThreadId]);

  useEffect(() => {
    const threadId = selectedThreadId ?? null;
    if (pendingInitialScrollThreadIdRef.current !== threadId) return;
    if (!threadId) return;

    if (chatLoading || pinsLoading) {
      initialScrollLoadSeenThreadIdRef.current = threadId;
      return;
    }

    const hasForeignItems = chatItems.some(item => item.thread_id !== threadId);
    const loadingWasSeen = initialScrollLoadSeenThreadIdRef.current === threadId;
    const hasItems = chatItems.length > 0;

    if (!loadingWasSeen && !hasItems) return;
    if (hasForeignItems && !loadingWasSeen) return;

    pendingInitialScrollThreadIdRef.current = null;
    const timer = setTimeout(() => {
      scrollToBottomDeferred();
      setShowScrollToBottom(false);
      previousMessagesCountRef.current = chatItems.length;
      previousTailItemIdRef.current = chatItems[chatItems.length - 1]?.id ?? null;
    }, 80);
    return () => clearTimeout(timer);
  }, [selectedThreadId, chatLoading, pinsLoading, chatItems, scrollToBottomDeferred]);

  useEffect(() => {
    const tailItemId = chatItems[chatItems.length - 1]?.id ?? null;
    if (!selectedThreadId || chatLoading) {
      previousMessagesCountRef.current = chatItems.length;
      previousTailItemIdRef.current = tailItemId;
      return;
    }

    const previousCount = previousMessagesCountRef.current;
    const previousTail = previousTailItemIdRef.current;
    const nextCount = chatItems.length;
    const tailChanged = Boolean(tailItemId && previousTail && tailItemId !== previousTail);
    previousMessagesCountRef.current = nextCount;
    previousTailItemIdRef.current = tailItemId;
    if (nextCount <= previousCount && !tailChanged) return;
    if (jumpToCommentUuid || loadingOlderComments) return;

    const viewport = viewportRef.current;
    if (!viewport || !isNearBottom(viewport)) return;

    const timer = setTimeout(() => {
      scrollToBottomDeferred();
      setShowScrollToBottom(false);
    }, 80);
    return () => clearTimeout(timer);
  }, [
    selectedThreadId,
    chatLoading,
    chatItems,
    jumpToCommentUuid,
    loadingOlderComments,
    isNearBottom,
    scrollToBottomDeferred,
  ]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!selectedThreadId || !viewport) return;

    const handleScroll = () => {
      if (chatLoading) return;
      if (viewport.scrollHeight <= viewport.clientHeight + 16) return;

      const now = Date.now();
      updateScrollToBottomVisibility(viewport);
      const nearTop = viewport.scrollTop <= 80;
      if (
        nearTop &&
        canAutoLoadOlder &&
        commentsHasMoreOlder &&
        !loadingOlderComments &&
        onLoadOlderComments &&
        now - lastTopAutoLoadAtRef.current > 400
      ) {
        lastTopAutoLoadAtRef.current = now;
        onLoadOlderComments();
        return;
      }

      const nearBottom = viewport.scrollHeight - (viewport.scrollTop + viewport.clientHeight) <= 80;
      if (
        nearBottom &&
        hasNewerMessages &&
        onLoadNewerComments &&
        now - lastBottomAutoLoadAtRef.current > 400
      ) {
        lastBottomAutoLoadAtRef.current = now;
        onLoadNewerComments();
      }
    };

    viewport.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      viewport.removeEventListener('scroll', handleScroll);
    };
  }, [
    selectedThreadId,
    chatLoading,
    updateScrollToBottomVisibility,
    canAutoLoadOlder,
    commentsHasMoreOlder,
    loadingOlderComments,
    onLoadOlderComments,
    hasNewerMessages,
    onLoadNewerComments,
  ]);

  useEffect(() => {
    if (!selectedThreadId || !jumpToCommentUuid || chatLoading) return;
    const viewport = viewportRef.current;
    if (!viewport) return;

    const candidates = viewport.querySelectorAll<HTMLElement>('[data-comment-uuid]');
    const target = Array.from(candidates).find(
      element => element.dataset.commentUuid === jumpToCommentUuid
    );
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    requestAnimationFrame(() => {
      updateScrollToBottomVisibility(viewport);
    });
    onJumpToCommentHandled?.();
  }, [
    selectedThreadId,
    jumpToCommentUuid,
    chatLoading,
    chatItems.length,
    updateScrollToBottomVisibility,
    onJumpToCommentHandled,
  ]);

  const scrollAfterSend = useCallback(() => {
    scrollToBottomSmooth();
    setShowScrollToBottom(false);
    setTimeout(() => {
      scrollToBottomDeferred();
      setShowScrollToBottom(false);
    }, 220);
  }, [scrollToBottomDeferred, scrollToBottomSmooth]);

  const scrollToBottomByUser = useCallback(() => {
    scrollToBottomSmooth();
    setShowScrollToBottom(false);
    setTimeout(() => {
      scrollToBottomDeferred(2);
      setShowScrollToBottom(false);
    }, 220);
  }, [scrollToBottomDeferred, scrollToBottomSmooth]);

  return {
    viewportRef,
    scrollAfterSend,
    showScrollToBottom,
    scrollToBottomByUser,
  };
}
